<template>
  <div class="editorjs-root tiptap-editor-root" :class="{ 'is-uploading': imageUploading }" @click="onRootClick">
    <div v-if="editor" class="tiptap-editor-toolbar" role="toolbar" aria-label="ابزارهای ویرایشگر">
      <!-- History -->
      <div class="tiptap-toolbar-group">
        <button type="button" class="tiptap-toolbar-btn" title="واگرد" :disabled="sourceMode || !editor.can().undo()" @click="editor.chain().focus().undo().run()">
          <svg class="tiptap-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 14 4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11"/></svg>
        </button>
        <button type="button" class="tiptap-toolbar-btn" title="بازگرد" :disabled="sourceMode || !editor.can().redo()" @click="editor.chain().focus().redo().run()">
          <svg class="tiptap-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="m15 14 5-5-5-5"/><path d="M20 9H9.5a5.5 5.5 0 0 0 0 11H13"/></svg>
        </button>
      </div>

      <div class="tiptap-toolbar-divider"></div>

      <!-- Heading -->
      <div class="tiptap-toolbar-dropdown" @click.stop>
        <button type="button" class="tiptap-toolbar-btn tiptap-toolbar-btn--wide" :class="{ active: openMenu === 'heading' }" :disabled="sourceMode" @click="toggleMenu('heading')">
          <span class="tiptap-heading-label">{{ currentHeadingLabel }}</span>
          <svg class="tiptap-icon tiptap-icon--xs" viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>
        </button>
        <div v-if="openMenu === 'heading'" class="tiptap-dropdown-panel">
          <button
            v-for="item in headingOptions"
            :key="item.id"
            type="button"
            class="tiptap-dropdown-item"
            :class="{ active: item.active }"
            @click="applyHeading(item)"
          >
            <span :class="item.previewClass">{{ item.label }}</span>
          </button>
        </div>
      </div>

      <div class="tiptap-toolbar-divider"></div>

      <!-- Alignment -->
      <div class="tiptap-toolbar-group">
        <button type="button" class="tiptap-toolbar-btn" :class="{ active: editor.isActive({ textAlign: 'right' }) }" title="راست‌چین" :disabled="sourceMode" @click="editor.chain().focus().setTextAlign('right').run()">
          <svg class="tiptap-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M21 6H3"/><path d="M21 12H9"/><path d="M21 18H3"/></svg>
        </button>
        <button type="button" class="tiptap-toolbar-btn" :class="{ active: editor.isActive({ textAlign: 'center' }) }" title="وسط‌چین" :disabled="sourceMode" @click="editor.chain().focus().setTextAlign('center').run()">
          <svg class="tiptap-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M21 6H3"/><path d="M17 12H7"/><path d="M21 18H3"/></svg>
        </button>
        <button type="button" class="tiptap-toolbar-btn" :class="{ active: editor.isActive({ textAlign: 'left' }) }" title="چپ‌چین" :disabled="sourceMode" @click="editor.chain().focus().setTextAlign('left').run()">
          <svg class="tiptap-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M21 6H3"/><path d="M15 12H3"/><path d="M21 18H3"/></svg>
        </button>
        <button type="button" class="tiptap-toolbar-btn" :class="{ active: editor.isActive({ textAlign: 'justify' }) }" title="تراز دوطرفه" :disabled="sourceMode" @click="editor.chain().focus().setTextAlign('justify').run()">
          <svg class="tiptap-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M21 6H3"/><path d="M21 12H3"/><path d="M21 18H3"/></svg>
        </button>
      </div>

      <div class="tiptap-toolbar-divider"></div>

      <!-- Formatting -->
      <div class="tiptap-toolbar-group">
        <button type="button" class="tiptap-toolbar-btn" :class="{ active: editor.isActive('bold') }" title="پررنگ" :disabled="sourceMode" @click="editor.chain().focus().toggleBold().run()">
          <svg class="tiptap-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M6 4h8a4 4 0 0 1 0 8H6z"/><path d="M6 12h9a4 4 0 0 1 0 8H6z"/></svg>
        </button>

        <div class="tiptap-toolbar-dropdown" @click.stop>
          <button type="button" class="tiptap-toolbar-btn" :class="{ active: openMenu === 'color' }" title="رنگ متن" :disabled="sourceMode" @click="toggleMenu('color')">
            <svg class="tiptap-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="m19 11-8-8-8.6 8.6a2 2 0 0 0 0 2.8l5.2 5.2c.8.8 2 .8 2.8 0L19 11Z"/><path d="m5 2 5 5"/><path d="M2 13h15"/><path d="M22 20a2 2 0 1 1-4 0c0-1.6 1.7-2.4 2-4 .3 1.6 2 2.4 2 4Z"/></svg>
            <span class="tiptap-color-indicator" :style="{ background: currentTextColor }"></span>
          </button>
          <div v-if="openMenu === 'color'" class="tiptap-dropdown-panel tiptap-color-panel">
            <div class="tiptap-color-grid">
              <button
                v-for="color in textColors"
                :key="color"
                type="button"
                class="tiptap-color-swatch"
                :style="{ background: color }"
                :title="color"
                @click="setTextColor(color)"
              ></button>
            </div>
            <label class="tiptap-color-custom">
              <span>رنگ سفارشی</span>
              <input type="color" :value="currentTextColor" @input="setTextColor($event.target.value)">
            </label>
            <button type="button" class="tiptap-dropdown-item" @click="unsetTextColor">حذف رنگ</button>
          </div>
        </div>

        <div class="tiptap-toolbar-dropdown" @click.stop>
          <button type="button" class="tiptap-toolbar-btn" :class="{ active: openMenu === 'highlight' || editor.isActive('highlight') }" title="هایلایت" :disabled="sourceMode" @click="toggleMenu('highlight')">
            <svg class="tiptap-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="m9 11-6 6v3h9l3-3"/><path d="m22 12-4.6 4.6a2 2 0 0 1-2.8 0l-5.2-5.2a2 2 0 0 1 0-2.8L14 4"/></svg>
          </button>
          <div v-if="openMenu === 'highlight'" class="tiptap-dropdown-panel tiptap-color-panel">
            <div class="tiptap-color-grid">
              <button
                v-for="color in highlightColors"
                :key="color"
                type="button"
                class="tiptap-color-swatch"
                :style="{ background: color }"
                :title="color"
                @click="setHighlight(color)"
              ></button>
            </div>
            <button type="button" class="tiptap-dropdown-item" @click="unsetHighlight">حذف هایلایت</button>
          </div>
        </div>
      </div>

      <div class="tiptap-toolbar-divider"></div>

      <!-- Font size -->
      <div class="tiptap-toolbar-dropdown" @click.stop>
        <button type="button" class="tiptap-toolbar-btn tiptap-toolbar-btn--wide" :class="{ active: openMenu === 'fontSize' }" title="سایز متن" :disabled="sourceMode" @click="toggleMenu('fontSize')">
          <svg class="tiptap-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M4 20h16"/><path d="m6 16 6-12 6 12"/><path d="M8 12h8"/></svg>
          <span class="tiptap-btn-text">{{ currentFontSizeLabel }}</span>
        </button>
        <div v-if="openMenu === 'fontSize'" class="tiptap-dropdown-panel">
          <button
            v-for="item in fontSizeOptions"
            :key="item.label"
            type="button"
            class="tiptap-dropdown-item"
            :class="{ active: currentFontSizeLabel === item.label }"
            @click="applyFontSize(item)"
          >
            <span :style="item.value ? { fontSize: item.value } : null">{{ item.label }}</span>
          </button>
        </div>
      </div>

      <div class="tiptap-toolbar-divider"></div>

      <!-- Insert -->
      <div class="tiptap-toolbar-group">
        <button type="button" class="tiptap-toolbar-btn" :class="{ active: editor.isActive('link') }" title="لینک" :disabled="sourceMode" @click="setLink">
          <svg class="tiptap-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>
        </button>
        <button type="button" class="tiptap-toolbar-btn" title="تصویر" :disabled="imageUploading || sourceMode" @click="openImagePicker">
          <svg class="tiptap-icon" viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-5-5L5 21"/></svg>
        </button>
 <button type="button" class="tiptap-toolbar-btn tiptap-toolbar-btn--wide" title="انتخاب از کتابخانه رسانه" :disabled="sourceMode" @click="mediaModal = true">
          <svg class="tiptap-icon" viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="5" width="14" height="14" rx="2"/><path d="M7 3h12a2 2 0 0 1 2 2v12"/><path d="m3 16 4-4 5 5"/></svg>
          <span class="tiptap-btn-text">رسانه</span>
        </button>
        <button type="button" class="tiptap-toolbar-btn" title="جدول" :disabled="sourceMode" @click="insertTable">
          <svg class="tiptap-icon" viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M3 15h18"/><path d="M9 3v18"/><path d="M15 3v18"/></svg>
        </button>
        <button type="button" class="tiptap-toolbar-btn tiptap-toolbar-btn--wide" :class="{ active: editor.isActive('htmlBlock') }" title="بلوک HTML" :disabled="sourceMode" @click="insertHtmlBlock">
          <svg class="tiptap-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M16 18l6-6-6-6"/><path d="M8 6l-6 6 6 6"/></svg>
          <span class="tiptap-btn-text">HTML</span>
        </button>
      </div>

      <div class="tiptap-toolbar-divider"></div>

      <!-- Source -->
      <div class="tiptap-toolbar-group">
        <button
          type="button"
          class="tiptap-toolbar-btn tiptap-toolbar-btn--wide"
          :class="{ active: sourceMode }"
          title="نمایش سورس کد"
          @click="toggleSourceMode"
        >
          <svg class="tiptap-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M16 18l6-6-6-6"/><path d="M8 6l-6 6 6 6"/></svg>
          <span class="tiptap-btn-text">{{ sourceMode ? 'ویرایشگر' : 'سورس کد' }}</span>
        </button>
      </div>
    </div>

    <div v-if="sourceMode" class="tiptap-source-wrap">
      <pre
        ref="sourceHighlight"
        class="tiptap-source-highlight"
        aria-hidden="true"
      ><code v-html="highlightedSourceHtml"></code></pre>
      <textarea
        ref="sourceTextarea"
        v-model="sourceHtml"
        class="tiptap-source-editor"
        dir="ltr"
        spellcheck="false"
        @input="onSourceInput"
        @scroll="syncSourceScroll"
      ></textarea>
    </div>
    <editor-content v-else :editor="editor" class="editorjs-holder tiptap-editor-holder border" />

    <CModal class="media-lib-modal" title="کتابخانه رسانه" color="dark" size="xl" centered :show.sync="mediaModal" :close-on-backdrop="false">
      <MediaLibraryBrowser v-if="mediaModal" picker multiple initial-type="image" :value="mediaSelected"
                           @input="mediaSelected = $event" @items="mediaItems = $event" @pick="insertFromLibrary"/>
      <template #footer>
        <CButton color="dark" variant="outline" @click="mediaModal = false">بستن</CButton>
        <CButton color="primary" @click="insertFromLibrary">درج در متن</CButton>
      </template>
    </CModal>

    <input
      ref="imageInput"
      type="file"
      accept="image/*"
      class="sr-only"
      @change="onImageSelected"
    >

    <div class="tiptap-portal">
      <div
        v-if="showImageContextMenu"
        class="tiptap-image-ctx-overlay"
        @click="closeImageContextMenu"
        @contextmenu.prevent="closeImageContextMenu"
      >
        <div
          class="tiptap-image-ctx"
          :style="imageContextMenuStyle"
          role="menu"
          @click.stop
        >
          <button type="button" class="tiptap-image-ctx__item" role="menuitem" @click="onContextEditImage">
            ویرایش
          </button>
          <div class="tiptap-image-ctx__divider"></div>
          <button
            v-for="item in imageAlignOptions"
            :key="item.value"
            type="button"
            class="tiptap-image-ctx__item"
            :class="{ active: currentImageAlign === item.value }"
            role="menuitem"
            @click="onContextAlignImage(item.value)"
          >
            {{ item.label }}
          </button>
          <div class="tiptap-image-ctx__divider"></div>
          <button type="button" class="tiptap-image-ctx__item tiptap-image-ctx__item--danger" role="menuitem" @click="onContextDeleteImage">
            حذف
          </button>
        </div>
      </div>

      <div
        v-if="showImageSettings"
        class="tiptap-image-modal-overlay"
        @click.self="closeImageSettings"
      >
        <div
          class="tiptap-image-modal"
          role="dialog"
          aria-modal="true"
          aria-label="تنظیمات تصویر"
          @click.stop
          @keydown.enter.exact.prevent="applyImageSettings"
        >
          <div class="tiptap-image-modal__header">
            <h3>تنظیمات تصویر</h3>
            <button type="button" class="tiptap-image-modal__close" title="بستن" @click="closeImageSettings">×</button>
          </div>
          <form class="tiptap-image-modal__form" @submit.prevent="applyImageSettings">
            <div v-if="imageSettings.src" class="tiptap-image-modal__preview">
              <img :src="imageSettings.src" :alt="imageSettings.alt || 'پیش‌نمایش'">
            </div>
            <div class="tiptap-image-modal__body">
              <label class="tiptap-image-field">
                <span>متن جایگزین (alt)</span>
                <input
                  v-model="imageSettings.alt"
                  type="text"
                  dir="rtl"
                  placeholder="توضیح کوتاه تصویر"
                >
              </label>
              <div class="tiptap-image-field-row">
                <label class="tiptap-image-field">
                  <span>عرض</span>
                  <input
                    v-model="imageSettings.width"
                    type="text"
                    dir="ltr"
                    placeholder="مثلاً 600px یا 50%"
                  >
                </label>
                <label class="tiptap-image-field">
                  <span>ارتفاع</span>
                  <input
                    v-model="imageSettings.height"
                    type="text"
                    dir="ltr"
                    placeholder="مثلاً 400px یا 40vh"
                  >
                </label>
              </div>
              <p class="tiptap-image-hint">واحدهای مجاز: px, %, vh, vw, rem, em و ... — عدد خالی = اندازه طبیعی</p>
            </div>
            <div class="tiptap-image-modal__footer">
              <button type="submit" class="tiptap-image-btn tiptap-image-btn--primary">ذخیره</button>
              <button type="button" class="tiptap-image-btn tiptap-image-btn--ghost" @click="closeImageSettings">انصراف</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "@/api/http";
import { Editor, EditorContent } from '@tiptap/vue-2'
import StarterKit from '@tiptap/starter-kit'
import Link from '@tiptap/extension-link'
import TextAlign from '@tiptap/extension-text-align'
import Placeholder from '@tiptap/extension-placeholder'
import Underline from '@tiptap/extension-underline'
import TextStyle from '@tiptap/extension-text-style'
import Color from '@tiptap/extension-color'
import Highlight from '@tiptap/extension-highlight'
import Table from '@tiptap/extension-table'
import TableRow from '@tiptap/extension-table-row'
import TableHeader from '@tiptap/extension-table-header'
import TableCell from '@tiptap/extension-table-cell'
import MediaLibraryBrowser from '@/views/media/MediaLibraryBrowser.vue'
import { highlightHtmlSource } from '@/utils/editor/htmlSourceHighlight'
import { FontSize, FONT_SIZE_OPTIONS } from '@/utils/editor/tiptapFontSize'
import { HtmlBlock, materializeHtmlBlocks } from '@/utils/editor/tiptapHtmlBlock'
import { beautifyHtml } from '@/utils/editor/beautifyHtml'
import {
  buildEditorContentPayload,
  resolveEditorInitialContent
} from '@/utils/editor/editorContent'
import {
  EditorImage,
  IMAGE_ALIGN_OPTIONS,
  normalizeImageSizeValue
} from '@/utils/editor/tiptapImage'

const TEXT_COLORS = ['#111827', '#374151', '#6b7280', '#dc2626', '#ea580c', '#ca8a04', '#16a34a', '#0891b2', '#2563eb', '#7c3aed', '#db2777']
const HIGHLIGHT_COLORS = ['#fef08a', '#fde68a', '#bbf7d0', '#bae6fd', '#ddd6fe', '#fbcfe8', '#fed7aa', '#e5e7eb']

import { imageScopeFor } from '@/utils/imageScope';
export default {
  name: 'RichTextEditor',
  components: { EditorContent, MediaLibraryBrowser },
  props: {
    first_content: { type: [Object, String], default: null },
    content_json: { type: [Object, String], default: null },
    content_text: { type: String, default: '' },
    content_html: { type: String, default: '' },
    editor_id: { type: String, default: 'tiptap_editor' }
  },
  data() {
    return {
      editor: null,
      imageUploading: false,
      showImageSettings: false,
      showImageContextMenu: false,
      imageContextPos: { left: 0, top: 0 },
      imageSettings: {
        src: '',
        alt: '',
        width: '',
        height: ''
      },
      imageAlignOptions: IMAGE_ALIGN_OPTIONS,
      openMenu: null,
      sourceMode: false,
      sourceHtml: '',
      isDestroyed: false,
      textColors: TEXT_COLORS,
      highlightColors: HIGHLIGHT_COLORS,
      fontSizeOptions: FONT_SIZE_OPTIONS,
      mediaModal: false,
      mediaSelected: [],
      mediaItems: []
    }
  },
  computed: {
    imageContextMenuStyle() {
      return {
        left: `${this.imageContextPos.left}px`,
        top: `${this.imageContextPos.top}px`
      }
    },
    currentImageAlign() {
      if (!this.editor || !this.editor.isActive('image')) return 'center'
      return this.editor.getAttributes('image').align || 'center'
    },
    currentHeadingLabel() {
      if (!this.editor) return 'متن'
      for (let level = 1; level <= 6; level += 1) {
        if (this.editor.isActive('heading', { level })) return `H${level}`
      }
      return 'متن'
    },
    currentFontSizeLabel() {
      if (!this.editor) return 'معمولی'
      const size = this.editor.getAttributes('textStyle').fontSize
      const found = FONT_SIZE_OPTIONS.find((item) => item.value === (size || ''))
      return found?.label || 'معمولی'
    },
    currentTextColor() {
      if (!this.editor) return '#111827'
      return this.editor.getAttributes('textStyle').color || '#111827'
    },
    highlightedSourceHtml() {
      return highlightHtmlSource(this.sourceHtml)
    },
    headingOptions() {
      if (!this.editor) return []
      const items = [
        { id: 'p', label: 'متن', previewClass: 'preview-p', active: this.editor.isActive('paragraph'), action: () => this.editor.chain().focus().setParagraph().run() }
      ]
      for (let level = 1; level <= 6; level += 1) {
        items.push({
          id: `h${level}`,
          label: `H${level}`,
          previewClass: `preview-h${level}`,
          active: this.editor.isActive('heading', { level }),
          action: () => this.editor.chain().focus().toggleHeading({ level }).run()
        })
      }
      return items
    }
  },
  mounted() {
    const initialHtml = resolveEditorInitialContent(this.first_content ?? this.content_json, this.content_html)

    this.editor = new Editor({
      extensions: [
        StarterKit.configure({
          heading: { levels: [1, 2, 3, 4, 5, 6] }
        }),
        Underline,
        TextStyle,
        Color,
        Highlight.configure({ multicolor: true }),
        FontSize,
        Link.configure({
          openOnClick: false,
          HTMLAttributes: { rel: 'noopener noreferrer nofollow', target: '_blank' }
        }),
        EditorImage.configure({
          HTMLAttributes: { class: 'tiptap-image' },
          allowBase64: false
        }),
        TextAlign.configure({
          types: ['heading', 'paragraph'],
          alignments: ['left', 'center', 'right', 'justify']
        }),
        Placeholder.configure({ placeholder: 'متن خود را اینجا بنویسید...' }),
        Table.configure({ resizable: false }),
        TableRow,
        TableHeader,
        TableCell,
        HtmlBlock
      ],
      content: initialHtml || '<p></p>',
      editorProps: {
        attributes: {
          id: this.editor_id,
          class: 'tiptap-editor__content',
          dir: 'rtl'
        },
        handleDOMEvents: {
          contextmenu: (view, event) => {
            const img = event.target?.closest?.('img')
            if (!img || this.sourceMode) return false
            event.preventDefault()
            this.selectImageAtDom(view, img)
            this.openImageContextMenu(event.clientX, event.clientY)
            return true
          }
        },
        handleDoubleClick: (_view, event) => {
          const img = event.target?.closest?.('img')
          if (!img || this.sourceMode) return false
          this.selectImageAtDom(_view, img)
          this.openImageSettings()
          return true
        }
      },
      onUpdate: ({ editor }) => this.emitEditorContent(editor),
      onSelectionUpdate: () => {
        this.$forceUpdate()
      }
    })

    this.$nextTick(() => {
      if (!this.isDestroyed) this.emitEditorContent(this.editor)
    })
  },
  watch: {
    content_html(newVal, oldVal) {
      if (!this.editor || this.sourceMode || this.isDestroyed) return
      const next = String(newVal || '').trim()
      const prev = String(oldVal || '').trim()
      if (!next || next === prev) return
      const current = String(this.editor.getHTML() || '').trim()
      if (next === current) return
      this.editor.commands.setContent(next, false)
    },
    first_content: {
      deep: true,
      handler(newVal) {
        if (!this.editor || this.sourceMode || this.isDestroyed) return
        if (String(this.content_html || '').trim()) return
        const html = resolveEditorInitialContent(newVal, '')
        if (!html) return
        const current = String(this.editor.getHTML() || '').trim()
        if (html === current || (current === '<p></p>' && !html)) return
        if (current && current !== '<p></p>') return
        this.editor.commands.setContent(html || '<p></p>', false)
        this.emitEditorContent(this.editor)
      }
    }
  },
  beforeDestroy() {
    this.isDestroyed = true
    if (this.sourceMode) {
      this.applySourceToEditor()
    }
    if (this.editor) {
      this.emitEditorContent(this.editor)
      this.editor.destroy()
      this.editor = null
    }
  },
  methods: {
    onRootClick() {
      this.openMenu = null
      this.closeImageContextMenu()
    },
    clampPanelPosition(x, y, width, height) {
      const pad = 8
      const maxLeft = Math.max(pad, window.innerWidth - width - pad)
      const maxTop = Math.max(pad, window.innerHeight - height - pad)
      return {
        left: Math.min(Math.max(pad, x), maxLeft),
        top: Math.min(Math.max(pad, y), maxTop)
      }
    },
    selectImageAtDom(view, img) {
      if (!view || !img || !this.editor) return
      try {
        const pos = view.posAtDOM(img, 0)
        this.editor.chain().setNodeSelection(pos).focus().run()
      } catch (_) {
        /* ignore invalid image positions */
      }
    },
    openImageContextMenu(clientX, clientY) {
      this.closeImageSettings()
      this.imageContextPos = this.clampPanelPosition(clientX, clientY, 170, 220)
      this.showImageContextMenu = true
      this.openMenu = null
    },
    closeImageContextMenu() {
      this.showImageContextMenu = false
    },
    onContextEditImage() {
      this.closeImageContextMenu()
      this.openImageSettings()
    },
    onContextAlignImage(align) {
      this.closeImageContextMenu()
      if (!this.editor || !this.editor.isActive('image')) return
      const resolved = align === 'left' || align === 'right' || align === 'center' ? align : 'center'
      this.editor.chain().focus().updateAttributes('image', { align: resolved }).run()
      this.emitEditorContent(this.editor)
    },
    onContextDeleteImage() {
      this.closeImageContextMenu()
      if (!this.editor || !this.editor.isActive('image')) return
      this.editor.chain().focus().deleteSelection().run()
      this.emitEditorContent(this.editor)
    },
    toggleMenu(name) {
      if (this.sourceMode) return
      this.openMenu = this.openMenu === name ? null : name
    },
    toggleSourceMode() {
      if (!this.editor) return
      this.openMenu = null
      this.closeImageContextMenu()
      this.closeImageSettings()

      if (this.sourceMode) {
        this.applySourceToEditor()
        this.sourceMode = false
        this.$nextTick(() => {
          this.editor?.commands.focus()
        })
        return
      }

      this.sourceHtml = beautifyHtml(materializeHtmlBlocks(this.editor.getHTML() || ''))
      this.sourceMode = true
      this.$nextTick(() => {
        this.$refs.sourceTextarea?.focus()
      })
    },
    applySourceToEditor() {
      if (!this.editor) return
      const html = String(this.sourceHtml || '').trim() || '<p></p>'
      this.editor.commands.setContent(html, { emitUpdate: true })
      this.emitEditorContent(this.editor)
    },
    onSourceInput() {
      if (this.isDestroyed) return
      const html = String(this.sourceHtml || '')
      const temp = document.createElement('div')
      temp.innerHTML = html
      this.$emit('update:content_html', html || '<p></p>')
      this.$emit('update:content_text', (temp.textContent || '').trim())
      this.$emit('update:content_json', buildEditorContentPayload(html || '<p></p>'))
    },
    syncSourceScroll(e) {
      const highlight = this.$refs.sourceHighlight
      if (!highlight) return
      highlight.scrollTop = e.target.scrollTop
      highlight.scrollLeft = e.target.scrollLeft
    },
    applyHeading(item) {
      item.action()
      this.openMenu = null
    },
    applyFontSize(item) {
      if (!this.editor) return
      if (!item.value) {
        this.editor.chain().focus().unsetFontSize().run()
      } else {
        this.editor.chain().focus().setFontSize(item.value).run()
      }
      this.openMenu = null
    },
    setTextColor(color) {
      if (!this.editor) return
      this.editor.chain().focus().setColor(color).run()
      this.openMenu = null
    },
    unsetTextColor() {
      if (!this.editor) return
      this.editor.chain().focus().unsetColor().run()
      this.openMenu = null
    },
    setHighlight(color) {
      if (!this.editor) return
      this.editor.chain().focus().toggleHighlight({ color }).run()
      this.openMenu = null
    },
    unsetHighlight() {
      if (!this.editor) return
      this.editor.chain().focus().unsetHighlight().run()
      this.openMenu = null
    },
    insertFromLibrary() {
      const items = (this.mediaItems || []).filter((it) => it && it.url)
      this.mediaModal = false
      if (!items.length || !this.editor) return
      let chain = this.editor.chain().focus()
      items.forEach((it) => { chain = chain.setImage({ src: it.url, alt: it.alt || it.title || '', align: 'center' }) })
      chain.run()
      this.mediaSelected = []
      this.mediaItems = []
      this.emitEditorContent(this.editor)
    },
    getUploadUrl() {
      return '/api/admin/media/upload?image_scope=' + (imageScopeFor(this.$route) || 'articles')
    },
    getEditorHtml(editor = this.editor) {
      if (!editor) return '<p></p>'
      return materializeHtmlBlocks(editor.getHTML() || '<p></p>')
    },
    emitEditorContent(editor) {
      if (!editor || this.isDestroyed) return
      const html = this.getEditorHtml(editor)
      this.$emit('update:content_html', html)
      this.$emit('update:content_text', editor.getText())
      this.$emit('update:content_json', buildEditorContentPayload(html))
    },
    setLink() {
      if (!this.editor || this.sourceMode) return
      const previousUrl = this.editor.getAttributes('link').href
      const url = window.prompt('آدرس لینک را وارد کنید', previousUrl || 'https://')
      if (url === null) return
      if (url === '') {
        this.editor.chain().focus().extendMarkRange('link').unsetLink().run()
        return
      }
      this.editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run()
    },
    insertTable() {
      if (!this.editor || this.sourceMode) return
      this.editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
    },
    insertHtmlBlock() {
      if (!this.editor || this.sourceMode) return
      this.editor.chain().focus().insertHtmlBlock('<div>\n  \n</div>').run()
    },
    openImagePicker() {
      if (this.sourceMode) return
      this.$refs.imageInput?.click()
    },
    openImageSettings() {
      if (!this.editor || this.sourceMode) return
      if (!this.editor.isActive('image')) return

      const attrs = this.editor.getAttributes('image') || {}
      this.imageSettings = {
        src: attrs.src || '',
        alt: attrs.alt || '',
        width: attrs.width != null && attrs.width !== '' ? String(attrs.width) : '',
        height: attrs.height != null && attrs.height !== '' ? String(attrs.height) : ''
      }

      this.showImageSettings = true
      this.closeImageContextMenu()
      this.openMenu = null
    },
    closeImageSettings() {
      this.showImageSettings = false
    },
    applyImageSettings() {
      if (!this.editor || !this.editor.isActive('image')) {
        this.showImageSettings = false
        return
      }

      const widthResult = normalizeImageSizeValue(this.imageSettings.width)
      const heightResult = normalizeImageSizeValue(this.imageSettings.height)

      if (!widthResult.ok) {
        alert('عرض نامعتبر است. از واحدهایی مثل px، %، vh، vw، rem استفاده کنید.')
        return
      }
      if (!heightResult.ok) {
        alert('ارتفاع نامعتبر است. از واحدهایی مثل px، %، vh، vw، rem استفاده کنید.')
        return
      }

      this.editor
        .chain()
        .focus()
        .updateAttributes('image', {
          alt: String(this.imageSettings.alt || '').trim() || null,
          width: widthResult.value,
          height: heightResult.value
        })
        .run()

      this.showImageSettings = false
      this.emitEditorContent(this.editor)
    },
    async onImageSelected(event) {
      const file = event.target?.files?.[0]
      if (event.target) event.target.value = ''
      if (!file || !this.editor) return

      if (!file.type.startsWith('image/')) {
        alert('فقط فایل تصویری مجاز است')
        return
      }

      const uploadUrl = this.getUploadUrl()
      if (!uploadUrl) {
        alert('آدرس آپلود تصویر تنظیم نشده است')
        return
      }

      this.imageUploading = true
      try {
        const formData = new FormData()
        formData.append('files', file)

        const response = await axios.post(uploadUrl, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          show_pros: false
        })

        const data = response?.data || {}
        const nested = data.data && typeof data.data === 'object' ? data.data : null
        if (data.error) throw new Error(data.msg || 'upload failed')
        const imageUrl =
          (Array.isArray(data.items) && data.items[0] && data.items[0].url) ||
          data.file?.url ||
          data.url ||
          data.Url ||
          data.path ||
          data.Path ||
          data.FileUrl ||
          data.fileUrl ||
          nested?.file?.url ||
          nested?.url ||
          nested?.Url ||
          nested?.FileUrl ||
          nested?.fileUrl ||
          ''

        if (!imageUrl) throw new Error('invalid upload response')
        this.editor.chain().focus().setImage({ src: imageUrl, alt: '', align: 'center' }).run()
        this.$nextTick(() => this.openImageSettings())
      } catch (_) {
        alert('خطا در آپلود تصویر')
      } finally {
        this.imageUploading = false
      }
    }
  }
}
</script>

<style>
.editorjs-root,
.tiptap-editor-root {
  max-width: 100%;
  min-width: 0;
  position: relative;
  box-sizing: border-box;
}

.tiptap-editor-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.2rem;
  padding: 0.45rem 0.55rem;
  margin-bottom: 0.65rem;
  border: 1px solid #e8e8ec;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
  direction: rtl;
}

.tiptap-toolbar-group {
  display: inline-flex;
  align-items: center;
  gap: 0.1rem;
}

.tiptap-toolbar-divider {
  width: 1px;
  height: 1.5rem;
  background: #ececf1;
  margin: 0 0.2rem;
  flex-shrink: 0;
}

.tiptap-toolbar-btn {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  min-width: 2rem;
  height: 2rem;
  padding: 0 0.4rem;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: #3f3f46;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.tiptap-toolbar-btn--wide {
  min-width: auto;
  padding: 0 0.55rem;
}

.tiptap-toolbar-btn:hover:not(:disabled) {
  background: #f4f4f5;
}

.tiptap-toolbar-btn.active {
  background: #eef2ff;
  color: #4338ca;
}

.tiptap-toolbar-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.tiptap-icon {
  width: 1.05rem;
  height: 1.05rem;
  stroke: currentColor;
  fill: none;
  flex-shrink: 0;
}

.tiptap-icon path,
.tiptap-icon rect,
.tiptap-icon circle {
  vector-effect: non-scaling-stroke;
}

.tiptap-icon--xs {
  width: 0.85rem;
  height: 0.85rem;
  opacity: 0.65;
}

.tiptap-heading-label,
.tiptap-btn-text {
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
}

.tiptap-color-indicator {
  position: absolute;
  bottom: 3px;
  left: 50%;
  transform: translateX(-50%);
  width: 0.7rem;
  height: 3px;
  border-radius: 999px;
}

.tiptap-toolbar-dropdown {
  position: relative;
}

.tiptap-dropdown-panel {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  z-index: 40;
  min-width: 10.5rem;
  padding: 0.35rem;
  border: 1px solid #e8e8ec;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.12);
}

.tiptap-dropdown-item {
  display: flex;
  width: 100%;
  align-items: center;
  padding: 0.45rem 0.6rem;
  border: none;
  border-radius: 7px;
  background: transparent;
  color: #27272a;
  font-size: 0.84rem;
  text-align: right;
  cursor: pointer;
}

.tiptap-dropdown-item:hover,
.tiptap-dropdown-item.active {
  background: #f4f4f5;
}

.tiptap-dropdown-item .preview-h1 { font-size: 1.2rem; font-weight: 800; }
.tiptap-dropdown-item .preview-h2 { font-size: 1.05rem; font-weight: 800; }
.tiptap-dropdown-item .preview-h3 { font-size: 0.95rem; font-weight: 700; }
.tiptap-dropdown-item .preview-h4 { font-size: 0.88rem; font-weight: 700; }
.tiptap-dropdown-item .preview-h5 { font-size: 0.82rem; font-weight: 700; }
.tiptap-dropdown-item .preview-h6 { font-size: 0.78rem; font-weight: 700; }
.tiptap-dropdown-item .preview-p { font-size: 0.84rem; }

.tiptap-color-panel {
  min-width: 12rem;
}

.tiptap-color-grid {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 0.35rem;
  padding: 0.25rem 0.35rem 0.5rem;
}

.tiptap-color-swatch {
  width: 1.35rem;
  height: 1.35rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  cursor: pointer;
  padding: 0;
}

.tiptap-color-swatch:hover {
  transform: scale(1.06);
}

.tiptap-color-custom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  padding: 0.35rem 0.55rem 0.5rem;
  font-size: 0.78rem;
  color: #6b7280;
}

.tiptap-color-custom input[type="color"] {
  width: 2rem;
  height: 1.5rem;
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
}

.editorjs-holder,
.tiptap-editor-holder {
  direction: rtl !important;
  text-align: right !important;
  max-width: 100%;
  min-height: 280px;
  border-radius: 12px;
  background: #fff;
}

.tiptap-source-wrap {
  position: relative;
  width: 100%;
  min-height: 320px;
  border: 1px solid #3c3c3c;
  border-radius: 12px;
  background: #1e1e1e;
  overflow: hidden;
  direction: ltr !important;
  text-align: left !important;
  box-sizing: border-box;
}

.tiptap-source-highlight,
.tiptap-source-editor {
  margin: 0;
  padding: 1rem 1.1rem;
  font-family: Consolas, 'Courier New', ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
  font-size: 0.875rem;
  line-height: 1.7;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  tab-size: 2;
  box-sizing: border-box;
  border: none;
  direction: ltr !important;
  text-align: left !important;
}

.tiptap-source-highlight {
  position: absolute;
  inset: 0;
  overflow: auto;
  pointer-events: none;
  color: #d4d4d4;
  background: transparent;
}

.tiptap-source-highlight code {
  display: block;
  font: inherit;
  white-space: inherit;
  word-wrap: inherit;
  color: inherit;
}

.tiptap-source-editor {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  min-height: 320px;
  background: transparent;
  color: transparent;
  -webkit-text-fill-color: transparent;
  caret-color: #aeafad;
  resize: vertical;
  outline: none;
  overflow: auto;
}

.tiptap-source-editor::selection {
  background: rgba(38, 79, 120, 0.55);
}

.tiptap-source-wrap:focus-within {
  border-color: #007acc;
  box-shadow: 0 0 0 1px #007acc;
}

.tiptap-source-wrap .tok-tag { color: #569cd6; }
.tiptap-source-wrap .tok-attr { color: #9cdcfe; }
.tiptap-source-wrap .tok-string { color: #ce9178; }
.tiptap-source-wrap .tok-comment { color: #6a9955; }
.tiptap-source-wrap .tok-punct { color: #808080; }
.tiptap-source-wrap .tok-text { color: #d4d4d4; }

.dark .tiptap-source-wrap {
  background: #1e1e1e;
  border-color: #3c3c3c;
}

.tiptap-editor-holder .tiptap-editor__content {
  min-height: 260px;
  padding: 1rem 1.1rem;
  outline: none;
  color: #12121f;
  line-height: 1.8;
  font-size: 1rem;
}

.tiptap-editor-holder .tiptap-editor__content:focus { outline: none; }

.tiptap-editor-holder .tiptap-editor__content p.is-editor-empty:first-child::before {
  content: attr(data-placeholder);
  float: right;
  color: #9ca3af;
  pointer-events: none;
  height: 0;
}

.tiptap-editor-holder .tiptap-editor__content h1,
.tiptap-editor-holder .tiptap-editor__content h2,
.tiptap-editor-holder .tiptap-editor__content h3,
.tiptap-editor-holder .tiptap-editor__content h4,
.tiptap-editor-holder .tiptap-editor__content h5,
.tiptap-editor-holder .tiptap-editor__content h6 {
  font-weight: 800;
  line-height: 1.45;
  margin: 1rem 0 0.5rem;
}

.tiptap-editor-holder .tiptap-editor__content h1 { font-size: 1.75rem; }
.tiptap-editor-holder .tiptap-editor__content h2 { font-size: 1.45rem; }
.tiptap-editor-holder .tiptap-editor__content h3 { font-size: 1.2rem; }
.tiptap-editor-holder .tiptap-editor__content h4 { font-size: 1.05rem; }
.tiptap-editor-holder .tiptap-editor__content h5 { font-size: 0.95rem; }
.tiptap-editor-holder .tiptap-editor__content h6 { font-size: 0.88rem; }

.tiptap-editor-holder .tiptap-editor__content p { margin: 0.55rem 0; }

.tiptap-editor-holder .tiptap-editor__content ul,
.tiptap-editor-holder .tiptap-editor__content ol {
  margin: 0.65rem 0;
  padding-right: 1.35rem;
  padding-left: 0;
}

.tiptap-editor-holder .tiptap-editor__content mark {
  border-radius: 0.2rem;
  padding: 0.05rem 0.15rem;
}

.tiptap-editor-holder .tiptap-editor__content a {
  color: #4338ca;
  text-decoration: underline;
}

.tiptap-editor-holder .tiptap-editor__content img,
.tiptap-editor-holder .tiptap-image {
  max-width: 100%;
  border-radius: 10px;
  margin-top: 0.75rem;
  margin-bottom: 0.75rem;
  display: block;
  cursor: pointer;
}

.tiptap-editor-holder .tiptap-editor__content img:not([style*="height"]),
.tiptap-editor-holder .tiptap-image:not([style*="height"]) {
  height: auto;
}

.tiptap-editor-holder .tiptap-editor__content img.ProseMirror-selectednode,
.tiptap-editor-holder .tiptap-image.ProseMirror-selectednode {
  outline: 2px solid #38bdf8;
  outline-offset: 2px;
}

.tiptap-image-ctx-overlay,
.tiptap-image-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 10050;
  background: transparent;
}

.tiptap-image-modal-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(15, 23, 42, 0.35);
}

.tiptap-image-ctx {
  position: fixed;
  min-width: 148px;
  padding: 0.3rem;
  border-radius: 10px;
  background: #fff;
  border: 1px solid #e5e7eb;
  box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16);
  direction: rtl;
  z-index: 10051;
}

.tiptap-image-ctx__item {
  display: block;
  width: 100%;
  border: 0;
  background: transparent;
  text-align: right;
  padding: 0.55rem 0.7rem;
  border-radius: 8px;
  font-size: 0.88rem;
  color: #1f2937;
  cursor: pointer;
}

.tiptap-image-ctx__item:hover,
.tiptap-image-ctx__item.active {
  background: #eef2ff;
  color: #4338ca;
}

.tiptap-image-ctx__item--danger {
  color: #dc2626;
}

.tiptap-image-ctx__item--danger:hover {
  background: #fef2f2;
  color: #dc2626;
}

.tiptap-image-ctx__divider {
  height: 1px;
  margin: 0.25rem 0.35rem;
  background: #e5e7eb;
}

.tiptap-image-modal {
  position: relative;
  width: min(420px, calc(100vw - 16px));
  max-height: calc(100vh - 32px);
  overflow: auto;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 20px 50px rgba(15, 23, 42, 0.2);
  direction: rtl;
  z-index: 10052;
}

.tiptap-image-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #e5e7eb;
}

.tiptap-image-modal__header h3 {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 700;
  color: #1f2937;
}

.tiptap-image-modal__close {
  border: 0;
  background: transparent;
  font-size: 1.4rem;
  line-height: 1;
  color: #6b7280;
  cursor: pointer;
  padding: 0.1rem 0.35rem;
}

.tiptap-image-modal__preview {
  padding: 0.85rem 1rem 0;
}

.tiptap-image-modal__preview img {
  display: block;
  max-width: 100%;
  max-height: 160px;
  margin: 0 auto;
  border-radius: 8px;
  object-fit: contain;
  background: #f8fafc;
}

.tiptap-image-modal__body {
  padding: 1rem;
  display: grid;
  gap: 0.75rem;
}

.tiptap-image-field {
  display: grid;
  gap: 0.35rem;
}

.tiptap-image-field span {
  font-size: 0.82rem;
  color: #4b5563;
}

.tiptap-image-field input {
  width: 100%;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 0.55rem 0.7rem;
  font-size: 0.9rem;
  background: #fff;
  color: #111827;
}

.tiptap-image-field input:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.tiptap-image-field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.65rem;
}

.tiptap-image-hint {
  margin: 0;
  font-size: 0.75rem;
  color: #6b7280;
}

.tiptap-image-modal__form {
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.tiptap-image-modal__footer {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 0.5rem;
  padding: 0 1rem 1rem;
}

.tiptap-image-btn {
  border: 0;
  border-radius: 8px;
  padding: 0.5rem 0.95rem;
  font-size: 0.875rem;
  cursor: pointer;
  line-height: 1.25;
}

button.tiptap-image-btn--ghost {
  background-color: #f3f4f6;
  color: #374151;
}

button.tiptap-image-btn--primary {
  background-color: var(--main-color, #111827);
  color: #fff;
}

button.tiptap-image-btn--primary:hover {
  filter: brightness(0.95);
}

.dark .tiptap-image-ctx,
.dark .tiptap-image-modal {
  background: #27272a;
  border-color: #3f3f46;
}

.dark .tiptap-image-ctx__item {
  color: #f4f4f5;
}

.dark .tiptap-image-ctx__item:hover,
.dark .tiptap-image-ctx__item.active {
  background: #312e81;
  color: #c7d2fe;
}

.dark .tiptap-image-ctx__divider {
  background: #3f3f46;
}

.dark .tiptap-image-modal__header {
  border-bottom-color: #3f3f46;
}

.dark .tiptap-image-modal__header h3,
.dark .tiptap-image-field span {
  color: #f4f4f5;
}

.dark .tiptap-image-field input {
  background: #18181b;
  border-color: #52525b;
  color: #f4f4f5;
}

.dark button.tiptap-image-btn--ghost {
  background-color: #3f3f46;
  color: #e4e4e7;
}

.tiptap-editor-holder .html-block-node.ProseMirror-selectednode,
.tiptap-editor-holder .html-block-node:has(.ProseMirror-selectednode) {
  outline: 2px solid #38bdf8;
  outline-offset: 1px;
}

.tiptap-editor-holder .tiptap-editor__content table {
  width: 100%;
  border-collapse: collapse;
  margin: 0.75rem 0;
  direction: rtl;
}

.tiptap-editor-holder .tiptap-editor__content th,
.tiptap-editor-holder .tiptap-editor__content td {
  border: 1px solid #e5e7eb;
  padding: 0.45rem 0.6rem;
  text-align: right;
  vertical-align: top;
}

.tiptap-editor-holder .tiptap-editor__content th {
  background: #f3f4f6;
  font-weight: 700;
}

.tiptap-editor-root.is-uploading { opacity: 0.92; }

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.dark .tiptap-editor-toolbar {
  background: #27272a;
  border-color: #3f3f46;
}

.dark .tiptap-toolbar-btn { color: #e4e4e7; }
.dark .tiptap-toolbar-btn:hover:not(:disabled) { background: #3f3f46; }
.dark .tiptap-toolbar-btn.active { background: #312e81; color: #c7d2fe; }
.dark .tiptap-dropdown-panel { background: #27272a; border-color: #3f3f46; }
.dark .tiptap-dropdown-item { color: #f4f4f5; }
.dark .tiptap-dropdown-item:hover,
.dark .tiptap-dropdown-item.active { background: #3f3f46; }
.dark .editorjs-holder,
.dark .tiptap-editor-holder { background: #18181b; border-color: #3f3f46; }
.dark .tiptap-editor-holder .tiptap-editor__content { color: #f4f4f5; }
</style>
